import { ThemesSource } from '@favordelivery/tupperware/lib/themes'
import { TuppThemeProvider } from '@favordelivery/tupperware/lib/themes/ThemeProvider'
import type { TuppTheme } from '@favordelivery/tupperware/lib/themes/types'
import { useState, useEffect } from 'react'
import { createGlobalStyle } from 'styled-components'
import type { ReactNode } from 'react'

const {
  colors: { light, dark },
  fonts: { montserrat }
} = ThemesSource

const customLight = {
  ...light,
  beta: {
    secondary: '#53626D',
    brandHeader: '#1E567E',
    bgBrown: '#F7F2E9',
    border: 'rgba(130, 154, 171, 0.70)',
    bgLight: '#FAF8F3'
  }
}

const customDark = {
  ...dark,
  beta: {
    secondary: '#F8F8F8',
    brandHeader: '#FFFFFF',
    bgBrown: '#262C33',
    border: 'rgba(130, 154, 171, 0.70)',
    bgLight: '#1E2328'
  }
}

const font = {
  ...montserrat,
  family: {
    ...montserrat.family,
    header: '"Anton", Tahoma, sans-serif'
  }
}

const theme = {
  light: customLight,
  dark: customDark,
  font
}

export const getTheme = theme => ({
  color: theme === 'light' ? customLight : customDark,
  font
})

const capitalize = str => str.charAt(0).toUpperCase() + str.slice(1)

const getVars = (name, obj) => {
  if (typeof obj === 'object') {
    return Object.keys(obj)
      .map(key => getVars(`${name}${capitalize(key)}`, obj[key]))
      .flat()
  } else {
    return { name, val: obj }
  }
}
const GlobalStyle = createGlobalStyle`
  html {
    box-sizing: border-box;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  html {
    font-size: 100%;
    -ms-overflow-style: scrollbar;
    -webkit-tap-highlight-color: rgba(0,0,0,0);
    height: 100%;
  }

  body {
    -moz-font-smoothing: grayscale;
    -moz-osx-font-smoothing: grayscale;
    -ms-text-size-adjust: 100%;
    -webkit-font-smoothing: antialiased;
    -webkit-text-size-adjust: 100%;
    background-color: #ffffff;
    color: #515f6e;
    font-family: Montserrat,Arial,Helvetica,sans-serif;
    font-size: 1rem;
    font-style: normal;
    line-height: 1.5rem;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  iframe {
    border: none;
  }

  h1,h2,h3,h4,h5,h6 {
    margin: 0;
    padding: 0;
  }

  a {
    text-decoration: none;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  input[type=number] {
    appearance: textfield;
  }

  iframe[name=indconv] {
    position: absolute;
  }

  #root {
    margin-bottom: -1px;
  }

  body {
    ${getVars('themeFont', theme.font).map(item => `--${item.name}: ${item.val};\n`)}
    @media (prefers-color-scheme: light) {
      ${getVars('themeColor', theme.light).map(item => `--${item.name}: ${item.val};\n`)}
    }
    @media (prefers-color-scheme: dark) {
      ${getVars('themeColor', theme.dark).map(item => `--${item.name}: ${item.val};\n`)}
    }
    background-color: var(--themeColorBackgroundPrimary);
    color: var(--themeColorForegroundPrimary);
  }
  a {
    color: var(--themeColorBluePrimary);
  }
`

export const BrowserThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<TuppTheme>('light')
  useEffect(() => {
    if (window && window.matchMedia) {
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
        setTheme(event.matches ? 'dark' : 'light')
      })
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        // eslint-ignore-next-line
        setTheme('dark')
      }
    }
  }, [])
  return (
    <TuppThemeProvider theme={theme} themeObject={getTheme(theme)}>
      <GlobalStyle />
      {children}
    </TuppThemeProvider>
  )
}

export default theme
